import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { NominatimService, GeocodeResult } from './nominatim.service';
import { MapboxService, RestaurantResult } from './mapbox.service';

export interface UnifiedPlace {
  id: string;
  name: string;
  address: string;
  location: {
    lat: number;
    lng: number;
  };
  category?: string;
  distance?: number;
  phone?: string;
  website?: string;
  source: 'nominatim' | 'mapbox';
  confidence: number;
}

export interface SearchOptions {
  lat?: number;
  lng?: number;
  radius?: number;
  limit?: number;
  category?: string;
  query?: string;
  useMapbox?: boolean;
  useNominatim?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MapsService {
  
  constructor(
    private nominatim: NominatimService,
    private mapbox: MapboxService
  ) {}

  /**
   * Búsqueda unificada de restaurantes
   * Combina resultados de Nominatim y Mapbox para mejores resultados
   */
  searchRestaurants(options: SearchOptions): Observable<UnifiedPlace[]> {
    const searches: Observable<UnifiedPlace[]>[] = [];

    // Búsqueda con Mapbox (datos comerciales ricos)
    if (options.useMapbox !== false && options.lat && options.lng) {
      const mapboxSearch = this.mapbox.searchNearbyRestaurants(
        options.lat, 
        options.lng, 
        {
          radius: options.radius,
          limit: options.limit || 10,
          category: options.category,
          query: options.query
        }
      ).pipe(
        map(results => results.map(r => this.transformMapboxToUnified(r))),
        catchError(() => of([]))
      );
      searches.push(mapboxSearch);
    }

    // Búsqueda con Nominatim (gratuito, datos básicos)
    if (options.useNominatim !== false) {
      const query = options.query || options.category || 'restaurant';
      const nominatimSearch = this.nominatim.searchByCategory(
        query,
        'restaurant',
        options.lat,
        options.lng,
        options.radius
      ).pipe(
        map(results => results.map(r => this.transformNominatimToUnified(r))),
        catchError(() => of([]))
      );
      searches.push(nominatimSearch);
    }

    if (searches.length === 0) {
      return of([]);
    }

    // Combinar resultados y eliminar duplicados
    return combineLatest(searches).pipe(
      map(resultArrays => {
        const allResults = resultArrays.flat();
        const uniqueResults = this.removeDuplicates(allResults);
        const sortedResults = this.sortByRelevance(uniqueResults, options);
        return sortedResults.slice(0, options.limit || 20);
      })
    );
  }

  /**
   * Geocoding unificado
   * Prioriza Nominatim para geocoding básico (gratis)
   */
  geocode(address: string, options?: {
    useMapbox?: boolean;
    proximity?: { lat: number; lng: number };
    country?: string;
  }): Observable<GeocodeResult[]> {
    // Primero intentar con Nominatim (gratis)
    return this.nominatim.geocode(address, {
      limit: 5,
      countrycodes: options?.country || 'es'
    }).pipe(
      switchMap(nominatimResults => {
        // Si Nominatim no da buenos resultados y tenemos Mapbox disponible
        if (nominatimResults.length === 0 && options?.useMapbox !== false) {
          return this.mapbox.geocode(address, {
            proximity: options?.proximity,
            country: options?.country,
            limit: 5
          });
        }
        return of(nominatimResults);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Reverse geocoding unificado
   */
  reverseGeocode(lat: number, lng: number, options?: {
    useMapbox?: boolean;
  }): Observable<GeocodeResult | null> {
    // Priorizar Nominatim para reverse geocoding
    return this.nominatim.reverseGeocode(lat, lng, {
      zoom: 18,
      addressdetails: true
    }).pipe(
      switchMap(result => {
        // Si no hay resultado y Mapbox está disponible, intentar con Mapbox
        if (!result && options?.useMapbox !== false) {
          return this.mapbox.geocode(`${lat},${lng}`, { limit: 1 }).pipe(
            map(results => results.length > 0 ? results[0] : null)
          );
        }
        return of(result);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Autocompletado de direcciones
   */
  autocomplete(query: string, options?: {
    proximity?: { lat: number; lng: number };
    useMapbox?: boolean;
    types?: string[];
  }): Observable<any[]> {
    if (query.length < 2) {
      return of([]);
    }

    // Combinar resultados de ambos servicios para mejor cobertura
    const searches: Observable<any[]>[] = [];

    // Nominatim autocomplete
    searches.push(
      this.nominatim.autocomplete(query, {
        countrycodes: 'es',
        limit: 4
      }).pipe(
        map(results => results.map(r => ({ ...r, source: 'nominatim' }))),
        catchError(() => of([]))
      )
    );

    // Mapbox autocomplete (si está disponible)
    if (options?.useMapbox !== false) {
      searches.push(
        this.mapbox.autocomplete(query, {
          proximity: options?.proximity,
          types: options?.types,
          country: 'es'
        }).pipe(
          map(results => results.map(r => ({ ...r, source: 'mapbox' }))),
          catchError(() => of([]))
        )
      );
    }

    return combineLatest(searches).pipe(
      map(resultArrays => {
        const allResults = resultArrays.flat();
        // Priorizar resultados de Mapbox por calidad
        return allResults
          .sort((a, b) => {
            if (a.source === 'mapbox' && b.source !== 'mapbox') return -1;
            if (b.source === 'mapbox' && a.source !== 'mapbox') return 1;
            return (b.relevance || 0) - (a.relevance || 0);
          })
          .slice(0, 8);
      })
    );
  }

  /**
   * Configurar tokens de API
   */
  configureServices(config: {
    mapboxToken?: string;
  }): void {
    if (config.mapboxToken) {
      this.mapbox.setAccessToken(config.mapboxToken);
    }
  }

  /**
   * Verificar disponibilidad de servicios
   */
  checkServices(): Observable<{
    nominatim: boolean;
    mapbox: boolean;
  }> {
    return combineLatest([
      this.nominatim.checkService(),
      this.mapbox.checkService()
    ]).pipe(
      map(([nominatimOk, mapboxOk]) => ({
        nominatim: nominatimOk,
        mapbox: mapboxOk
      })),
      catchError(() => of({ nominatim: false, mapbox: false }))
    );
  }

  /**
   * Transformar resultado de Mapbox a formato unificado
   */
  private transformMapboxToUnified(result: RestaurantResult): UnifiedPlace {
    return {
      id: `mapbox_${result.id}`,
      name: result.name,
      address: result.address,
      location: result.location,
      category: result.category,
      distance: result.distance,
      phone: result.phone,
      website: result.website,
      source: 'mapbox',
      confidence: result.relevance
    };
  }

  /**
   * Transformar resultado de Nominatim a formato unificado
   */
  private transformNominatimToUnified(result: GeocodeResult): UnifiedPlace {
    return {
      id: `nominatim_${result.place_id}`,
      name: this.extractNameFromAddress(result.address),
      address: result.formattedAddress,
      location: {
        lat: result.lat,
        lng: result.lng
      },
      category: 'Restaurante',
      source: 'nominatim',
      confidence: result.confidence
    };
  }

  /**
   * Extraer nombre del lugar de la dirección
   */
  private extractNameFromAddress(address: string): string {
    // Intentar extraer el nombre del lugar de la dirección completa
    const parts = address.split(',');
    return parts[0].trim();
  }

  /**
   * Eliminar duplicados basados en proximidad y nombre
   */
  private removeDuplicates(places: UnifiedPlace[]): UnifiedPlace[] {
    const unique: UnifiedPlace[] = [];
    const threshold = 0.0001; // Aproximadamente 10 metros

    for (const place of places) {
      const isDuplicate = unique.some(existing => {
        const distance = Math.abs(existing.location.lat - place.location.lat) +
                        Math.abs(existing.location.lng - place.location.lng);
        const namesSimilar = this.areNamesSimilar(existing.name, place.name);
        
        return distance < threshold && namesSimilar;
      });

      if (!isDuplicate) {
        unique.push(place);
      } else {
        // Si es duplicado, mantener el de mejor calidad (Mapbox > Nominatim)
        const existingIndex = unique.findIndex(existing => {
          const distance = Math.abs(existing.location.lat - place.location.lat) +
                          Math.abs(existing.location.lng - place.location.lng);
          return distance < threshold && this.areNamesSimilar(existing.name, place.name);
        });

        if (existingIndex !== -1 && place.source === 'mapbox' && unique[existingIndex].source === 'nominatim') {
          unique[existingIndex] = place;
        }
      }
    }

    return unique;
  }

  /**
   * Verificar si dos nombres son similares
   */
  private areNamesSimilar(name1: string, name2: string): boolean {
    const normalize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
    const n1 = normalize(name1);
    const n2 = normalize(name2);
    
    return n1.includes(n2) || n2.includes(n1) || n1 === n2;
  }

  /**
   * Ordenar resultados por relevancia
   */
  private sortByRelevance(places: UnifiedPlace[], options: SearchOptions): UnifiedPlace[] {
    return places.sort((a, b) => {
      // Priorizar por distancia si está disponible
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      
      // Priorizar por fuente (Mapbox > Nominatim)
      if (a.source === 'mapbox' && b.source === 'nominatim') return -1;
      if (a.source === 'nominatim' && b.source === 'mapbox') return 1;
      
      // Finalmente por confianza
      return (b.confidence || 0) - (a.confidence || 0);
    });
  }
}